import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { ItemPrice } from "@/lib/erp";
import { useNavigate } from "react-router-dom";
import { itemPriceQueryKeys } from "./itemPriceQueryKeys";
import { pricingSummaryKeys } from "./pricingSummaryHooks";
import { buildDetailRoute } from "@/lib/config/docRoutes";

export interface UseCreateItemPriceOptions {
  /** When false, skip navigation to detail page after creation. Default: true */
  navigateOnSuccess?: boolean;
  /** Item code — used to invalidate the allItemPrices cache for inline refresh */
  itemCode?: string;
  /** Custom success callback (only called when navigateOnSuccess is false) */
  onSuccess?: (data: ItemPrice) => void;
}

/**
 * Hook for creating a new item price
 *
 * @returns React Query mutation for creating item price
 */
export function useCreateItemPrice(options?: UseCreateItemPriceOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const navigateOnSuccess = options?.navigateOnSuccess ?? true;

  return useMutation<ItemPrice, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return erp.itemPrices.create(data);
    },
    onSuccess: (data) => {
      const itemPriceName = data.name;

      // Update cache for the new item price
      if (itemPriceName) {
        queryClient.setQueryData(itemPriceQueryKeys.detail(itemPriceName), data);
      }

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: itemPriceQueryKeys.list() });

      // Also invalidate the allItemPrices cache (used by PriceListTab)
      if (options?.itemCode) {
        queryClient.invalidateQueries({
          queryKey: pricingSummaryKeys.allItemPrices(options.itemCode),
        });
      }

      if (navigateOnSuccess) {
        // Redirect to detail page (existing behavior)
        if (itemPriceName) {
          navigate(buildDetailRoute("Item Price", itemPriceName));
        }
      } else {
        // Inline mode — call custom callback, no navigation
        options?.onSuccess?.(data);
      }
    },
  });
}

